import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import ClaimCommunicationInfoImg from '../../../../assets/img/info-icon.svg';
import SMSIcon from '../../../../assets/svg/SMSIcon';
import { HeaderContent } from '../../../../components/HeaderContent';
import { Project } from '../../../../domain/project/Project';
import { ProjectCustomerSMSTypes } from '../../../../domain/project/ProjectDTO';
import { Subscription } from '../../../../domain/subscription/Subscription';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveSupportDocs } from '../../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { intercom } from '../../../../services/intercom';
import { NotificationsHeaderContainer } from '../Notifications.styled';
import { NotificationsSMSEnableDialog } from './NotificationsSMSEnableDialog';
import { NotificationsSMSTemplate } from './NotificationsSMSTemplate';

type NotificationsSMSProps = {
  project: Project;
  organizationSubscription?: Subscription;
};

export const NotificationsSMS = ({ project, organizationSubscription }: NotificationsSMSProps) => {
  const { data: user } = useRetrieveUser();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const [isEnableSMSDialogOpen, setIsEnableSMSDialogOpen] = useState<boolean>(false);
  const [projectCustomerSMS, setProjectCustomerSMS] = useState<ProjectCustomerSMSTypes[]>([]);
  const [selectedSMSType, setSelectedSMSType] = useState<ProjectCustomerSMSTypes | undefined>();

  useEffect(() => {
    if (project?.customer_sms) {
      const trackingSMS: ProjectCustomerSMSTypes[] = project.getCustomerAvailableSMSTypes();

      if (!selectedSMSType && trackingSMS.length > 0) {
        setSelectedSMSType(trackingSMS[0]);
      }
      setProjectCustomerSMS(trackingSMS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const isTemplateEnabled = (smsType: ProjectCustomerSMSTypes) => {
    return organizationSubscription?.sms.enabled && project.isSMSEnabled(smsType);
  };

  if (!project) return null;

  return (
    <>
      <Grid container direction="column" wrap="nowrap" style={{ minWidth: `1200px`, height: ` 100%` }}>
        <NotificationsHeaderContainer>
          <HeaderContent
            titleIntlId="projects.communication.messages.sms.title"
            subtitle="projects.communication.messages.sms.subtitle"
            article={{
              intlId: `projects.communication.messages.sms.article`,
              link: retrieveDoc({ supportPage: `communicationSMS`, language: user?.language.value }),
            }}
            styles={{
              container: {
                padding: `11px 20px`,
                backgroundColor: RoutalPalette.neutral02n,
                flex: `80%`,
              },
            }}
          />

          <Grid
            container
            direction="column"
            justifyContent="center"
            wrap="nowrap"
            alignItems="flex-end"
            style={{
              padding: `16px 22px`,
              flex: `20%`,
              height: `100%`,
            }}
          >
            {!organizationSubscription?.sms.enabled ? (
              <Button
                intlId="projects.communication.messages.sms.need_administrator.header"
                onClick={() => {
                  setIsEnableSMSDialogOpen(true);
                }}
              />
            ) : (
              <Grid item container direction="column" wrap="nowrap" alignItems="flex-end">
                <RoutalTooltip
                  title={
                    <Text
                      style={{ color: RoutalPalette.neutral00 }}
                      intlId="projects.communication.messages.sms.remaining_sms"
                      intlValues={{ number: organizationSubscription.sms.credit }}
                    />
                  }
                  placement="left"
                >
                  <Grid item container direction="row" wrap="nowrap" justifyContent="flex-end" alignItems="center">
                    <Text style={{ margin: `0 8px` }}>{organizationSubscription.sms.credit}</Text>
                    <SMSIcon style={{ width: `14px` }} />
                  </Grid>
                </RoutalTooltip>

                <VSpacer small />

                <Grid item>
                  <Text
                    variant="button"
                    intlId="projects.communication.messages.sms.more_sms"
                    onClick={() => {
                      intercom.message(intl.formatMessage({ id: `projects.communication.messages.sms.more_sms.text` }));
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </NotificationsHeaderContainer>

        <Grid
          container
          direction="row"
          wrap="nowrap"
          style={{
            height: `calc(100% - 117px)`,
            backgroundColor: RoutalPalette.neutral00,
          }}
        >
          <Grid
            item
            container
            direction="column"
            style={{
              width: `380px`,
              borderRight: `1px solid ${RoutalPalette.grey4}`,
              overflowY: `auto`,
            }}
            wrap="nowrap"
          >
            <Grid container direction="column">
              <Grid item container style={{ height: `70px`, padding: `12px 16px` }} direction="row" alignItems="center">
                <Grid item>
                  <Text
                    intlId="projects.communication.messages.active_messages"
                    intlValues={{
                      active: project.countCustomerEnabledSMS(),
                      total: project.countCustomerAvailableSMS(),
                    }}
                  />
                </Grid>
              </Grid>

              {projectCustomerSMS.map((smsType: any, index: number) => (
                <Grid
                  key={`sms-${index}`}
                  container
                  direction="row"
                  style={{
                    cursor: `pointer`,
                    padding: `4px`,
                    paddingRight: `16px`,
                    height: `52px`,
                    borderBottom: `1px solid ${RoutalPalette.grey4}`,
                    backgroundColor: selectedSMSType === smsType ? RoutalPalette.primary40n : RoutalPalette.neutral00,
                    ...(index === 0 ? { borderTop: `1px solid ${RoutalPalette.grey4}` } : { borderTop: `unset` }),
                  }}
                  onClick={() => {
                    setSelectedSMSType(undefined);
                    setTimeout(() => {
                      setSelectedSMSType(smsType);
                    });
                  }}
                  wrap="nowrap"
                >
                  <div
                    style={{
                      width: `4px`,
                      height: `100%`,
                      backgroundColor: isTemplateEnabled(smsType)
                        ? RoutalPalette.success.medium
                        : RoutalPalette.neutral20,
                      borderRadius: `4px`,
                    }}
                  />

                  <HSpacer medium />

                  <Grid item container direction="row" wrap="nowrap" alignItems="center" justifyContent="space-between">
                    <Grid
                      item
                      container
                      direction="row"
                      wrap="nowrap"
                      alignItems="center"
                      style={{ maxWidth: `225px` }}
                    >
                      <Text
                        isBold
                        tagName="span"
                        style={{ cursor: `pointer` }}
                        intlId={`projects.communication.messages.sms.${project.getTranslationsSMSType(smsType)}`}
                      />

                      <HSpacer small />

                      <RoutalTooltip
                        title={
                          <Text
                            style={{ color: RoutalPalette.neutral00 }}
                            intlId={`projects.communication.messages.sms.${project.getTranslationsSMSType(
                              smsType
                            )}.description`}
                          />
                        }
                      >
                        <div style={{ marginTop: `5px` }}>
                          <img src={ClaimCommunicationInfoImg} alt="" height="16" width="16" draggable="false" />
                        </div>
                      </RoutalTooltip>
                    </Grid>

                    <Grid>
                      <Text
                        tagName="span"
                        isBold
                        style={{
                          cursor: `pointer`,
                          color: project.isSMSEnabled(smsType) ? RoutalPalette.success.medium : RoutalPalette.neutral20,
                        }}
                        intlId={isTemplateEnabled(smsType) ? `enabled` : `disabled`}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item container direction="column" style={{ flex: `1`, height: `100%` }}>
            {selectedSMSType ? (
              <NotificationsSMSTemplate
                smsType={selectedSMSType}
                project={project}
                organizationSubscription={organizationSubscription}
              />
            ) : null}
          </Grid>
        </Grid>
      </Grid>

      {isEnableSMSDialogOpen ? (
        <NotificationsSMSEnableDialog
          open={Boolean(isEnableSMSDialogOpen)}
          onClose={() => {
            setIsEnableSMSDialogOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
